/**
 * Cabeçalho do tema novo (Eternità 2026): menu, painel "Produtos" e rolagem.
 *
 * - aria-expanded dos botões que abrem o menu (#e-nav) e a busca (#e-search)
 *   acompanha o <dialog>. Ao fechar, se o foco se perdeu (no <body>, porque o
 *   Safari não foca botões no clique, ou num dialog fechado), ele volta ao
 *   botão visível que abre o dialog.
 * - Painel "Produtos" do desktop (<details data-e-mega>): fecha com Esc, com
 *   clique fora e quando o foco sai dele.
 * - Celular e tablet: o cabeçalho some ao descer e volta ao subir (NN/g,
 *   cabeçalho parcialmente fixo). Não some com movimento reduzido, perto do
 *   topo, com foco de teclado nele nem com painel ou gaveta aberta.
 * - Painel da conta (<shopify-account>, contas novas): abre no pé real do
 *   cabeçalho, somando a faixa de avisos quando ela ainda está à vista.
 * - Menu do celular: busca e carrinho passam a vez ([data-e-nav-handoff]).
 *
 * Abrir e fechar os dialogs é do commandfor (polyfill em e-core.js).
 */

const DIALOG_IDS = ['e-nav', 'e-search'];
const SCROLL_THRESHOLD = 6;
const compactViewport = window.matchMedia('(max-width: 63.99em)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const watchedDialogs = new WeakSet();

const openersOf = (dialog) => document.querySelectorAll(`[aria-controls="${dialog.id}"]`);
const headerSection = () => document.querySelector('.e-section-header');

/* ---------- Menu e busca (<dialog>) ---------- */
function syncExpanded(dialog) {
  openersOf(dialog).forEach((button) => button.setAttribute('aria-expanded', String(dialog.open)));
}

/* Foco perdido: no <body> ou ainda num dialog que acabou de fechar (a busca
   aberta pelo menu: o abridor dela saiu junto com o menu, e o Chrome só
   manda o foco ao <body> no quadro seguinte). O abridor que recebe o foco
   também não pode estar num dialog fechado, ainda visível na saída. */
function restoreFocus(dialog) {
  const focused = document.activeElement;
  if (focused && focused !== document.body && !focused.closest('dialog:not([open])')) return;
  const opener = [...openersOf(dialog)].find(
    (button) => button.getClientRects().length > 0 && !button.closest('dialog:not([open])'),
  );
  opener?.focus();
}

function watchDialogs() {
  for (const id of DIALOG_IDS) {
    const dialog = document.getElementById(id);
    if (!dialog || watchedDialogs.has(dialog)) continue;
    watchedDialogs.add(dialog);
    new MutationObserver(() => syncExpanded(dialog)).observe(dialog, {
      attributes: true,
      attributeFilter: ['open'],
    });
    dialog.addEventListener('close', () => restoreFocus(dialog));
    syncExpanded(dialog);
  }
}

/* ---------- Painel "Produtos" (<details>) ---------- */
const openPanels = () => document.querySelectorAll('[data-e-mega][open]');

function closePanel(panel, returnFocus) {
  panel.open = false;
  if (returnFocus) panel.querySelector('summary')?.focus();
}

function closePanelsOutside(event) {
  openPanels().forEach((panel) => {
    if (!panel.contains(event.target)) closePanel(panel, false);
  });
}

function watchPanels() {
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    openPanels().forEach((panel) => closePanel(panel, panel.contains(document.activeElement)));
  });
  document.addEventListener('pointerdown', closePanelsOutside);
  document.addEventListener('focusin', closePanelsOutside);
}

/* ---------- Cabeçalho parcialmente fixo ---------- */
function isHeaderBusy(section) {
  return (
    section.querySelector(':focus-visible, [data-e-mega][open]') !== null ||
    document.querySelector('.e-drawer[open]') !== null
  );
}

function autoHideHeader() {
  let lastY = Math.max(window.scrollY, 0);
  let ticking = false;

  const update = () => {
    ticking = false;
    const section = headerSection();
    if (!section) return;
    const y = Math.max(window.scrollY, 0);
    const delta = y - lastY;
    if (Math.abs(delta) < SCROLL_THRESHOLD) return;
    lastY = y;
    const hide =
      delta > 0 &&
      compactViewport.matches &&
      !reducedMotion.matches &&
      y > section.offsetHeight * 3 &&
      !isHeaderBusy(section);
    section.toggleAttribute('data-e-hidden', hide);
  };

  const show = () => headerSection()?.removeAttribute('data-e-hidden');

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    },
    { passive: true },
  );
  compactViewport.addEventListener('change', show);
  reducedMotion.addEventListener('change', show);
  document.addEventListener('focusin', (event) => {
    if (headerSection()?.contains(event.target)) show();
  });
}

/* ---------- Painel da conta (contas novas) ---------- */
/* O componente põe o painel a --shopify-account-dialog-position-top do topo da
   janela; o CSS dá a altura do cabeçalho fixo (e-header.css). Perto do topo, a
   faixa de avisos ainda está acima do cabeçalho: no clique, antes de o painel
   abrir, vale o pé do cabeçalho medido naquele instante. */
function placeAccountPanel() {
  document.addEventListener(
    'click',
    (event) => {
      const account = event.target.closest?.('.e-header__account--new');
      const section = headerSection();
      if (!account || !section) return;
      const bottom = Math.round(section.getBoundingClientRect().bottom);
      account.style.setProperty('--shopify-account-dialog-position-top', `${bottom}px`);
    },
    true,
  );
}

/* ---------- Menu: busca e carrinho passam a vez ----------
   O toque na busca ou no carrinho do menu fecha o menu no quadro seguinte,
   depois que o destino abriu (o command do botão e o openCart rodam no
   clique): uma gaveta sobre a outra escureceria o fundo duas vezes, e o menu
   sai pela esquerda enquanto o destino entra. Com o foco já no destino, o
   fechamento não o move; quando o destino fecha, o restoreFocus (busca) e o
   e-cart.js (carrinho) devolvem o foco ao botão visível do cabeçalho. */
function handOffFromMenu() {
  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('#e-nav [data-e-nav-handoff]')) return;
    requestAnimationFrame(() => document.getElementById('e-nav')?.close());
  });
}

/* ---------- Voltar pelo histórico não reabre o menu ---------- */
function closeOnPageHide() {
  window.addEventListener('pagehide', () => {
    DIALOG_IDS.forEach((id) => document.getElementById(id)?.close());
    openPanels().forEach((panel) => closePanel(panel, false));
  });
}

/* ---------- Editor de temas: bloco escolhido no menu fica visível ---------- */
function followEditorSelection() {
  document.addEventListener('shopify:block:select', (event) => {
    const dialog = event.target.closest?.('#e-nav');
    if (dialog && !dialog.open) dialog.show();
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    event.target.closest?.('#e-nav')?.close();
  });
  document.addEventListener('shopify:section:load', watchDialogs);
}

watchDialogs();
watchPanels();
autoHideHeader();
placeAccountPanel();
handOffFromMenu();
closeOnPageHide();
followEditorSelection();
